import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { authAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const OTPPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const phone = searchParams.get('phone');
  const { login } = useAuth();

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [resending, setResending] = useState(false);
  
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!phone) {
      navigate('/auth/login');
    }
  }, [phone, navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = pastedData.split('');
    while (newOtp.length < 6) newOtp.push('');
    setOtp(newOtp);
    if (pastedData.length === 6) {
      inputRefs.current[5]?.focus();
    }
  };

  const isComplete = otp.every(digit => digit !== '');

  const handleVerify = async () => {
    if (!isComplete) {
      setError('Enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authAPI.verifyOTP(phone, otp.join(''));
      const { token, user, is_new_user } = response.data;
      
      login(token, user);
      
      // Redirect based on whether user is new (needs profile setup)
      if (is_new_user || !user.name) {
        navigate('/profile?setup=true');
      } else {
        navigate('/home');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Incorrect OTP. Please try again.';
      setError(message);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || resending) return;

    setResending(true);
    try {
      await authAPI.requestOTP(phone);
      setResendTimer(30);
      setError('');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to resend OTP';
      setError(message);
    } finally {
      setResending(false);
    }
  };

  const formatPhone = (number) => {
    if (!number) return '';
    return `+91 ${number.slice(0, 5)} ${number.slice(5)}`;
  };

  return (
    <div className="min-h-screen bg-white flex flex-col p-6">
      {/* Header */}
      <button 
        onClick={() => navigate('/auth/login')}
        className="flex items-center gap-2 text-black font-bold mb-8 hover:opacity-70 transition-opacity"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </button>

      <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto w-full">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold mb-2">
            Verify your number
          </h1>
          <p className="text-neutral-600">
            We sent a code via WhatsApp to
          </p>
          <p className="font-bold text-lg">{formatPhone(phone)}</p>
        </div>

        {/* OTP Input */}
        <div className="flex gap-3 mb-4" onPaste={handlePaste}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl transition-all
                ${error ? 'border-error animate-shake' : 'border-black'}
                focus:outline-none focus:ring-2 focus:ring-black`}
              disabled={loading}
              autoFocus={index === 0}
              data-testid={`otp-input-${index}`}
            />
          ))}
        </div>

        {error && (
          <p className="text-error text-sm font-medium mb-4" data-testid="error-message">
            {error}
          </p>
        )}

        {/* Resend */}
        <div className="text-center mb-8">
          <p className="text-neutral-500 text-sm">
            Didn't receive code?{' '}
            {resendTimer > 0 ? (
              <span>Resend in 0:{resendTimer.toString().padStart(2, '0')}</span>
            ) : (
              <button
                onClick={handleResend}
                disabled={resending}
                className="text-black font-bold underline hover:opacity-70"
                data-testid="resend-button"
              >
                {resending ? 'Sending...' : 'Resend'}
              </button>
            )}
          </p>
        </div>

        {/* Verify Button */}
        <button
          onClick={handleVerify}
          disabled={!isComplete || loading}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="verify-button"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Verifying...
            </>
          ) : (
            <>
              Verify
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OTPPage;
